import { watch } from 'vue';
import moment from 'moment';
import { autoSaveEncounterNoteApi, updateEncounterNoteApi } from '@/api/encounterNotes';

/**
 * Composable for auto-saving encounter note content and metadata.
 *
 * @param {Object} options
 * @param {import('vue').ComputedRef<string>} options.noteId - Current note ID
 * @param {import('vue').ComputedRef<boolean>} options.isDraft - Whether note is unsigned
 * @param {import('vue').Ref<string>} options.noteContent - Rich HTML content
 * @param {import('vue').Ref<string>} options.encounterTitle - Note title
 * @param {import('vue').Ref<string>} options.appointmentDate - Service date
 * @param {import('vue').Ref<string>} options.appointmentType - Appointment type
 * @param {import('vue').Ref<number>} options.contentVersion - Optimistic concurrency version
 * @param {import('vue').Ref<boolean>} options.isInitializing - True while note data is loading
 * @param {import('vue').Ref<string>} options.saveStatus - Shared save status ref
 * @param {import('vue').Ref<string>} options.lastLoadedContent - Shared last loaded content ref
 * @param {import('vue').Ref<string|null>} options.lockedAt - Lock session timestamp from acquire-edit-lock
 * @param {Function} [options.onLockConflict] - Called when a 409 conflict is received (lock lost)
 */
export function useAutoSave({
  noteId,
  isDraft,
  noteContent,
  encounterTitle,
  appointmentDate,
  appointmentType,
  contentVersion,
  isInitializing,
  saveStatus,
  lastLoadedContent,
  lockedAt,
  onLockConflict,
}) {
  let isCleaning = false;

  /**
   * Strip "[Auto]" prefix from title if present, updating the ref and returning the cleaned title.
   */
  const cleanAutoTitle = () => {
    const title = encounterTitle.value || '';
    if (title.startsWith('[Auto]')) {
      const cleaned = title.replace(/^\[Auto\]\s*/, '');
      isCleaning = true;
      encounterTitle.value = cleaned;
      isCleaning = false;
      return cleaned;
    }
    return title;
  };

  /**
   * Save note content via auto-save API
   */
  const saveNote = async () => {
    const currentNoteId = noteId.value;
    if (!currentNoteId) return;
    if (!isDraft.value) return;
    if (noteContent.value === lastLoadedContent.value) return;

    saveStatus.value = 'saving';

    const needsTitleClean = (encounterTitle.value || '').startsWith('[Auto]');

    try {
      const payload = {
        content: noteContent.value,
        content_version: contentVersion.value,
      };
      payload.lock_session_at = lockedAt?.value || null;

      const response = await autoSaveEncounterNoteApi(currentNoteId, payload);
      if (response && response.content_version) {
        contentVersion.value = response.content_version;
      }
      lastLoadedContent.value = noteContent.value;
      saveStatus.value = 'saved';

      // Auto-save is content-only; title cleanup requires a PUT call
      if (needsTitleClean) {
        cleanAutoTitle();
        await updateNoteMetadata();
      }
    } catch (error) {
      if (error.response?.status === 409) {
        saveStatus.value = 'conflict';
        onLockConflict?.();
        return;
      }
      console.error('Auto-save failed', error);
      saveStatus.value = 'error';
    }
  };

  /**
   * Update note metadata (title, date, type) via PUT API
   */
  const updateNoteMetadata = async () => {
    const currentNoteId = noteId.value;
    if (!currentNoteId) return;
    if (!isDraft.value) return;

    try {
      const title = cleanAutoTitle();
      const payload = {
        title,
        service_date: appointmentDate.value ? moment(appointmentDate.value).toISOString() : null,
        appointment_type: appointmentType.value,
        content_version: contentVersion.value,
      };
      payload.lock_session_at = lockedAt?.value || null;

      const response = await updateEncounterNoteApi(currentNoteId, payload);
      if (response && response.content_version) {
        contentVersion.value = response.content_version;
      }
    } catch (error) {
      if (error.response?.status === 409) {
        saveStatus.value = 'conflict';
        onLockConflict?.();
        return;
      }
      console.error('Failed to update note metadata', error);
    }
  };

  // Watch metadata changes to auto-update
  watch(
    [encounterTitle, appointmentDate, appointmentType],
    () => {
      if (isInitializing.value) return;
      if (isCleaning) return;
      if (noteId.value) {
        updateNoteMetadata();
      }
    }
  );

  return {
    saveNote,
    updateNoteMetadata,
  };
}
